/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, LoadingSpinner, mixins } from '@blend/consumer-components';
import { useRootSelector } from '../../../hooks/useRootSelector';
import SigningDocument from './SigningDocument';
import { DocumentPackage } from './DocumentPackage';

export namespace Documents {
  export interface Props {
    handleDocumentClick: (documentId: string) => void;
    handleEditDocumentClick?: (documentId: string) => void;
    handleFinalizeDocumentClick?: (documentId: string) => void;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
    ':last-child': {
      borderBottom: 'none',
    },
  },
  headingWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: colors.catalystLight01,
    ...mixins.padding(8),
    ...mixins.borderTop(1, 'solid', mixins.globalBorderColor),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropdownIcon: {
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.4s ease-out',
    ...mixins.margin(0, 10, 0, 0),
    ...mixins.padding(0),
  },
  dropdownCollapsedIcon: {
    transform: 'rotate(-90deg)',
  },
});

export const Documents: React.FC<Documents.Props> = ({
  handleDocumentClick = () => {},
  handleEditDocumentClick,
  handleFinalizeDocumentClick,
}) => {
  const { document } = useRootSelector(state => state);
  const { documents } = document;

  const documentsGroupedByEnvelope: Record<string, SigningDocument[]> = {};

  documents.forEach(d => {
    documentsGroupedByEnvelope[d.providerEnvelopeId] = documentsGroupedByEnvelope[d.providerEnvelopeId] || [];
    documentsGroupedByEnvelope[d.providerEnvelopeId].push(d);
  });

  return (
    <div className={css(styles.wrapper)} data-it="Documents">
      {!documents ? (
        <div className={css(styles.loadingWrapper)}>
          <LoadingSpinner size={'sm'} color={colors.catalyst} />
        </div>
      ) : (
        <>
          {Object.values(documentsGroupedByEnvelope).map((envelopeDocuments, index) => {
            return (
              <DocumentPackage
                key={`package${index + 1}`}
                handleDocumentClick={handleDocumentClick}
                handleEditDocumentClick={handleEditDocumentClick}
                handleFinalizeDocumentClick={handleFinalizeDocumentClick}
                documents={envelopeDocuments}
                selectedDoc={document.selectedDoc}
                packageIndex={index}
              />
            );
          })}
        </>
      )}
    </div>
  );
};
